import { Capacitor, registerPlugin } from '@capacitor/core'

const PhoneGpsRecorderNative = registerPlugin('PhoneGpsRecorder')

export const PHONE_GPS_RECORDING_MAX_DURATION_SECONDS = 2 * 60 * 60

export const hasReachedPhoneGpsRecordingDurationLimit = (
    startedAt,
    now = Date.now(),
    maxDurationSeconds = PHONE_GPS_RECORDING_MAX_DURATION_SECONDS
) => {
    const startedAtMs = Number(startedAt)
    const nowMs = Number(now)
    const maxDurationMs = Number(maxDurationSeconds) * 1000

    return (
        Number.isFinite(startedAtMs) &&
        startedAtMs > 0 &&
        Number.isFinite(nowMs) &&
        Number.isFinite(maxDurationMs) &&
        maxDurationMs > 0 &&
        nowMs - startedAtMs >= maxDurationMs
    )
}

export const PHONE_GPS_RECORDER_STATUSES = Object.freeze({
    IDLE: 'idle',
    RECORDING: 'recording',
    STOPPED: 'stopped',
    INTERRUPTED: 'interrupted',
    UNAVAILABLE: 'unavailable',
})

const fallbackState = () => ({
    status: PHONE_GPS_RECORDER_STATUSES.UNAVAILABLE,
    recordingUuid: null,
    startedAt: null,
    endedAt: null,
    pointsCount: 0,
    lastPointAt: null,
    totalDistanceMeters: 0,
    currentSpeedMps: null,
    filename: null,
    manifestPath: null,
    stopReason: null,
})

const isAvailable = () => Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('PhoneGpsRecorder')

const checkPermissions = async () => {
    if (!isAvailable()) {
        return { location: 'unavailable', coarseLocation: 'unavailable' }
    }

    return await PhoneGpsRecorderNative.checkPermissions()
}

const requestPermissions = async () => {
    if (!isAvailable()) {
        return { location: 'unavailable', coarseLocation: 'unavailable' }
    }

    return await PhoneGpsRecorderNative.requestPermissions()
}

const startRecording = async (options = {}) => {
    if (!isAvailable()) {
        return fallbackState()
    }

    return await PhoneGpsRecorderNative.startRecording(options)
}

const stopRecording = async () => {
    if (!isAvailable()) {
        return fallbackState()
    }

    return await PhoneGpsRecorderNative.stopRecording()
}

const getState = async () => {
    if (!isAvailable()) {
        return fallbackState()
    }

    return await PhoneGpsRecorderNative.getState()
}

const exportRecording = async ({ recordingUuid } = {}) => {
    if (!isAvailable()) {
        return { manifestPath: null, filename: null, sourceText: null }
    }

    return await PhoneGpsRecorderNative.exportRecording({ recordingUuid })
}

const deleteRecording = async ({ recordingUuid } = {}) => {
    if (!isAvailable()) return

    await PhoneGpsRecorderNative.deleteRecording({ recordingUuid })
}

const addListener = (eventName, listener) => PhoneGpsRecorderNative.addListener(eventName, listener)

const removeAllListeners = async () => {
    if (!isAvailable() || typeof PhoneGpsRecorderNative.removeAllListeners !== 'function') return

    await PhoneGpsRecorderNative.removeAllListeners()
}

export default {
    isAvailable,
    checkPermissions,
    requestPermissions,
    startRecording,
    stopRecording,
    getState,
    exportRecording,
    deleteRecording,
    addListener,
    removeAllListeners,
}
